import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { CheckCircle, AlertTriangle, X } from 'lucide-react';

type TToastType = 'success' | 'error';

interface IToastItem {
  id: number;
  type: TToastType;
  message: string;
}

interface IToastContextValue {
  success: (message: string) => void;
  error: (message: string) => void;
}

const ToastContext = createContext<IToastContextValue>({
  success: () => {},
  error: () => {},
});

/** Access the toast API: `const toast = useToast(); toast.success('Done')`. */
export function useToast(): IToastContextValue {
  return useContext(ToastContext);
}

const AUTO_DISMISS_MS = 4500;

/**
 * Minimal, dependency-free toast provider. Renders a fixed stack (top-right) and
 * auto-dismisses each toast. Wrap the app so any screen can call `useToast()`.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<IToastItem[]>([]);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (type: TToastType, message: string) => {
      const id = Date.now() + Math.random();
      setToasts((prev) => [...prev, { id, type, message }]);
      window.setTimeout(() => remove(id), AUTO_DISMISS_MS);
    },
    [remove],
  );

  const value = useMemo<IToastContextValue>(
    () => ({
      success: (message: string) => push('success', message),
      error: (message: string) => push('error', message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed top-4 right-4 z-[60] flex flex-col gap-2 w-full max-w-sm pointer-events-none">
        {toasts.map((t) => (
          <div
            key={t.id}
            role="status"
            className="pointer-events-auto card px-4 py-3 flex items-start gap-3 animate-slide-up shadow-lg"
          >
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                t.type === 'success' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
              }`}
            >
              {t.type === 'success' ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <AlertTriangle className="w-4 h-4" />
              )}
            </div>
            <p className="flex-1 text-sm text-ink-secondary leading-snug">{t.message}</p>
            <button
              onClick={() => remove(t.id)}
              className="text-ink-tertiary hover:text-ink transition-colors flex-shrink-0"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
